import { pickBy, isEmpty, size } from 'lodash';
import { setIn, getIn, push } from 'icepick';
import uuid from 'uuid';
import Logger from '@cvent/nucleus-logging';
import { hideLoadingOnError } from
  'nucleus-guestside-site/src/redux/modules/dialogContainer';
import { loadAvailableCapacityCounts } from '../../capacity';
import { defaultRegistrationTypeId } from 'event-widgets/utils/registrationType';
import { getAdmissionItems, getSessions, isRegistrationModification,
  getSelectedAdmissionItem, getGuestsOfRegistrant } from './selectors';
import { updateAdmissionItemRegistration, updateSessionRegistration,
  getInitialProductRegistrations, setAdminRegOnProductionSelection } from './internal';
import { UPDATE_REG_CART_PENDING, UPDATE_REG_CART_FAILURE,
  UPDATE_REG_CART_SUCCESS, SET_CURRENT_GUEST_EVENT_REGISTRATION,
  CLEAR_CURRENT_GUEST_EVENT_REGISTRATION } from './actionTypes';
import { SAVING_REGISTRATION } from '../../registrationIntents';
import { calculatePricing } from './pricing';
import { getUpdateErrors, findKnownErrorResourceKey } from '../errors';
import { openKnownErrorDialog } from '../../../dialogs';
import { getDefaultAdmissionItemIdSelectionForRegType } from '../../selectors/event';
import { isProductVisibleForEventRegistration, getEventRegistrationId } from '../../selectors/currentRegistrant';
import { isGuestProductSelectionEnabledOnRegPath } from '../../selectors/currentRegistrationPath';
import { populateVisibleProducts } from '../../visibleProducts';

const LOG = new Logger('redux/registrationForm/regCart/guests');

function updateRegCartWithGuestsAdmissionItem(regCart, currentRegistrantId, lastSavedRegCart, productId) {
  // Get all admission items i.e admission item with 'REGISTER' and 'UNREGISTER' as requested action
  const currentRegistrantAdmissionItems = getAdmissionItems(regCart, currentRegistrantId);
  const primaryAdmissionItem = getSelectedAdmissionItem(regCart, currentRegistrantId);
  const currentRegistrantGuests = getGuestsOfRegistrant(
    regCart, currentRegistrantId);
  const primaryLastSavedAdmissionItem = getSelectedAdmissionItem(lastSavedRegCart, currentRegistrantId);
  const isRegMod = isRegistrationModification(regCart);
  if (currentRegistrantGuests.length === 0 || !currentRegistrantAdmissionItems
    || isEmpty(currentRegistrantAdmissionItems) || !primaryAdmissionItem) {
    return regCart;
  }
  const primaryChoseSameAdmissionItem = productId === primaryAdmissionItem.productId ?
    primaryLastSavedAdmissionItem && primaryLastSavedAdmissionItem.productId === primaryAdmissionItem.productId : true;
  let regCartWithGuestUpdates = regCart;
  currentRegistrantGuests.forEach(
    (guestReg) => {
      const guestSelectedAdmissionItem = getSelectedAdmissionItem(regCart, guestReg.eventRegistrationId);
      const shouldUpdatePreviousGuestSelections = productId === primaryAdmissionItem.productId ?
        (guestSelectedAdmissionItem && guestSelectedAdmissionItem.productId !== productId)
        && primaryChoseSameAdmissionItem : false;
      /**
       * FLEX-25013 - During regMod only a guest who previously registered for a adm item can get the
       * adm item back if the primary doesn't have visibility to the adm item. Since we don't pass in productId
       * when we set a adm item to unregister. If the adm item is set to 'UNREGISTER' and has matching productId
       * that means it was reselected by primary so give it back to the guest who had previously registered
       */
      const guestAdmissionItems = getAdmissionItems(regCart, guestReg.eventRegistrationId);
      const reselectAdmissionItem = Object.values(currentRegistrantAdmissionItems).some((admissionItem) => {
        const admissionItemContainedByGuest = guestAdmissionItems &&
          guestAdmissionItems.hasOwnProperty(admissionItem.productId);
        const isAdmissionItemBeingUnregistered = admissionItemContainedByGuest && admissionItem.requestedAction === 'UNREGISTER';
        return isRegMod && isAdmissionItemBeingUnregistered && productId === admissionItem.productId;
      });
      if (!guestSelectedAdmissionItem || !primaryChoseSameAdmissionItem || shouldUpdatePreviousGuestSelections
        || reselectAdmissionItem) {
        const guestRegUpdates = {
          ...guestReg,
          ...updateAdmissionItemRegistration(regCart, guestReg.eventRegistrationId, reselectAdmissionItem ? productId :
            primaryAdmissionItem.productId)
        };
        regCartWithGuestUpdates = setIn(regCartWithGuestUpdates, ['eventRegistrations',
          guestReg.eventRegistrationId], guestRegUpdates);
      }
    });
  return regCartWithGuestUpdates;
}

const updateGuestIncludedSessions = (guestRegEventRegId, guestIncludedSessions, regCartToBeUpdated) => {
  let regCartWithGuestUpdates = regCartToBeUpdated;
  Object.values(guestIncludedSessions).forEach((includedSession) => {
    regCartWithGuestUpdates = setIn(regCartWithGuestUpdates, ['eventRegistrations',
      guestRegEventRegId, 'sessionRegistrations',
      includedSession.productId, 'requestedAction'], 'REGISTER');
  });
  return regCartWithGuestUpdates;
};

function updateRegCartWithGuestsSessions(regCart, currentRegistrantId, lastSavedRegCart, productId,
  isSessionVisibleToPrimary) {
  // Copy all sessions to guest, sessions with both 'UNREGISTER' and 'REGISTER' as requested action are copied.
  const currentRegistrantAllSessions = getSessions(regCart, currentRegistrantId);
  const currentRegistrantGuests = getGuestsOfRegistrant(
    regCart, currentRegistrantId);
  const isRegMod = isRegistrationModification(regCart);
  /**
   * For FLEX-21900 - This gets the previously registered items so we don't change those selections if switching paths
   * from where guests choose their own items to guest get the same as the primary
   */
  const currentRegistrantLastSavedRegisteredGuests = getGuestsOfRegistrant(lastSavedRegCart, currentRegistrantId);
  // If no sessions to Register / unregistered, nothing to do here, exit
  if (currentRegistrantGuests.length === 0 || !currentRegistrantAllSessions
    || isEmpty(currentRegistrantAllSessions)) {
    return regCart;
  }
  // get all optional sessions which are being registered / unregistered for by primary
  const primaryOptionalSessions = pickBy(currentRegistrantAllSessions, (session) => {
    const sessionAssociatedToAdmissionItem = session.registrationSourceType === 'AdmissionItem';
    const includedSessions = session.registrationSourceType === 'Included';
    return !sessionAssociatedToAdmissionItem && !includedSessions;
  });
  let regCartWithGuestUpdates = regCart;
  currentRegistrantGuests.forEach(
    guestReg => {
      let updatedGuestReg =
        regCartWithGuestUpdates.eventRegistrations[guestReg.eventRegistrationId];
      // if new guest copy all sessions from the primary
      const isNewGuest = !currentRegistrantLastSavedRegisteredGuests
        .find(eventReg => eventReg.eventRegistrationId === guestReg.eventRegistrationId);
      // copy all optional sessions which are being registered and unregister any sessions for guest
      Object.values(primaryOptionalSessions).forEach((session) => {
        const sessionContainedByGuest = updatedGuestReg.sessionRegistrations
          && updatedGuestReg.sessionRegistrations.hasOwnProperty(session.productId);
        const isSessionBeingUnregistered = sessionContainedByGuest && session.requestedAction === 'UNREGISTER';
        const isNewOrRegisteredSession = session.requestedAction === 'REGISTER';
        // if the session wasn't previously registered for register it for guests already in the cart
        const shouldUpdatePreviousGuestSelections = session.productId === productId
          && session.requestedAction === 'REGISTER';
        /**
         * FLEX-25013 - During regMod only a guest who previously registered for a session can get the
         * session back if the primary doesn't have visibility to the session. Since we don't pass in productId
         * when we set a session to unregister. If the session is set to 'UNREGISTER' and has matching productId
         * that means it was reselected by primary so give it back to the guest with previously registered
         */
        const reselectSession = isRegMod && productId === session.productId && isSessionBeingUnregistered
          && !isSessionVisibleToPrimary;
        if (isSessionBeingUnregistered || (isNewGuest && isNewOrRegisteredSession)
          || shouldUpdatePreviousGuestSelections) {
          const guestRegUpdates = {
            ...updatedGuestReg,
            ...updateSessionRegistration(updatedGuestReg.sessionRegistrations,
              session.productId, isNewOrRegisteredSession || reselectSession ? 'REGISTER' : 'UNREGISTER')
          };
          regCartWithGuestUpdates = setIn(regCartWithGuestUpdates, ['eventRegistrations',
            guestReg.eventRegistrationId], guestRegUpdates);
          updatedGuestReg =
            regCartWithGuestUpdates.eventRegistrations[guestReg.eventRegistrationId];
        }
      });
    });
  return regCartWithGuestUpdates;
}

export function updateGuestsToMatchPrimaryReg(
  regCart,
  currentRegistrantId,
  state,
  productId) {
  let updatedRegCart = regCart;

  const { regCartStatus: { lastSavedRegCart } } = state;
  const isSessionVisibleToPrimary = isProductVisibleForEventRegistration(state.visibleProducts,
    productId, currentRegistrantId);
  const isGuestProductSelectionEnabled = isGuestProductSelectionEnabledOnRegPath(state);
  if (!isGuestProductSelectionEnabled) {
    updatedRegCart = updateRegCartWithGuestsAdmissionItem(updatedRegCart, currentRegistrantId,
      lastSavedRegCart, productId);
    updatedRegCart = updateRegCartWithGuestsSessions(updatedRegCart, currentRegistrantId,
      lastSavedRegCart, productId, isSessionVisibleToPrimary);
  }

  return updatedRegCart;
}

function addGuestsToRegCart(state, eventId, regCart, guestCount,
  currentRegistrantId, translate, nullRegType) {
  /*
    While adding guests:
      1) first change guests with 'UNREGISTER' requested action to 'REGISTER' and update their products
        to the primary's
      2) If more guests need to be added, add new event reg objects
  */
  let regCartWithGuestUpdates = regCart;

  let currentRegistrantGuestCount = getGuestsOfRegistrant(
    regCart, currentRegistrantId).length;

  const currentRegistrantGuestCountWithUnregisterAction = getGuestsOfRegistrant(
    regCart, currentRegistrantId, 'UNREGISTER').length;

  // Change status first
  const numberOfGuestsToAdd = Math.min(guestCount - currentRegistrantGuestCount,
    currentRegistrantGuestCountWithUnregisterAction);

  for (let i = 0; i < numberOfGuestsToAdd; i++) {
    const guestReg = Object.values(regCartWithGuestUpdates.eventRegistrations).find(
      eventReg => eventReg.attendeeType === 'GUEST' && eventReg.requestedAction === 'UNREGISTER'
      && eventReg.primaryRegistrationId === currentRegistrantId);
    regCartWithGuestUpdates = setIn(regCartWithGuestUpdates, ['eventRegistrations',
      guestReg.eventRegistrationId, 'requestedAction'],
    'REGISTER'
    );
    // for guests changed from UNREGISTER to REGISTER, re-register their included sessions
    const guestIncludedSessions = pickBy(guestReg.sessionRegistrations, (session) => {
      const isIncludedSession = session.registrationSourceType === 'Included';
      return isIncludedSession;
    });
    regCartWithGuestUpdates = updateGuestIncludedSessions(guestReg.eventRegistrationId,
      guestIncludedSessions, regCartWithGuestUpdates);
  }
  // Get updated guest  count
  currentRegistrantGuestCount = getGuestsOfRegistrant(regCartWithGuestUpdates, currentRegistrantId).length;

  // Add new eventReg objects for guests if needed
  for (let i = 0; i < guestCount - currentRegistrantGuestCount; i++) {
    // The last name is going to be set to "Guest XX" which gets picked up by the reg API
    const translatedGuestText = translate('Registration_RegistrationSummary_GuestPlaceholderName__resx', {
      count: (currentRegistrantGuestCount + i + 1).toString().padStart(2, '0')
    });
    const newEventRegId = String(uuid.v4());

    const primaryAdmissionItem = getSelectedAdmissionItem(regCartWithGuestUpdates, currentRegistrantId);
    const productRegistrations = !isGuestProductSelectionEnabledOnRegPath(state) ?
      primaryAdmissionItem && [primaryAdmissionItem] || [] : getInitialProductRegistrations(state);
    regCartWithGuestUpdates = setIn(regCartWithGuestUpdates, ['eventRegistrations', newEventRegId],
      {
        eventId,
        eventRegistrationId: newEventRegId,
        attendee: {
          personalInformation: {
            lastName: translatedGuestText,
            customFields: {}
          },
          eventAnswers: {}
        },
        attendeeType: 'GUEST',
        primaryRegistrationId: currentRegistrantId,
        productRegistrations: nullRegType ? [] : productRegistrations,
        requestedAction: 'REGISTER',
        displaySequence: currentRegistrantGuestCount + i + 1,
        sessionRegistrations: {},
        registrationTypeId: nullRegType ? null : defaultRegistrationTypeId
      }
    );
  }
  return regCartWithGuestUpdates;
}

export function removeGuestByEventRegistrationId(regCart, eventRegistrationIdToRemove) {
  return setIn(regCart, ['eventRegistrations',
    eventRegistrationIdToRemove, 'requestedAction'], 'UNREGISTER');
}

function removeGuestsFromRegCart(event, eventId, regCart, guestCount, currentRegistrantId) {
  let regCartWithGuestUpdates = regCart;
  let currentRegistrantsGuests = getGuestsOfRegistrant(regCart, currentRegistrantId);
  const guestsToRemove = currentRegistrantsGuests.length - guestCount;
  for (let i = 0; i < guestsToRemove; i++) {
    regCartWithGuestUpdates = removeGuestByEventRegistrationId(regCartWithGuestUpdates,
      currentRegistrantsGuests.pop().eventRegistrationId);
  }
  return regCartWithGuestUpdates;
}

export function updateRegCartWithGuests(state, eventId, regCart, guestCount,
  currentRegistrantId, translate, nullRegType = false) {
  const currentRegistrantGuestCount = getGuestsOfRegistrant(regCart, currentRegistrantId).length;
  // TODO: Sort guests by displaySequence once we start persisting them across sessions

  let regCartWithGuestUpdates = regCart;
  if (currentRegistrantGuestCount === guestCount) {
    return updateGuestsToMatchPrimaryReg(regCart, currentRegistrantId, state);
  }

  if (currentRegistrantGuestCount < guestCount) {
    regCartWithGuestUpdates = addGuestsToRegCart(
      state, eventId, regCart, guestCount, currentRegistrantId, translate, nullRegType);
  }

  if (currentRegistrantGuestCount > guestCount) {
    regCartWithGuestUpdates = removeGuestsFromRegCart(state.event, eventId, regCart, guestCount, currentRegistrantId);
  }
  return updateGuestsToMatchPrimaryReg(regCartWithGuestUpdates, currentRegistrantId, state);
}

/**
 * Action to set the current guest info to be edited/added
 * @param {Object} guestEventRegistration event registration of guest to be edited/added
 */
export function setCurrentGuestEventRegistration(guestEventRegistration) {
  const { attendee: { personalInformation } } = guestEventRegistration;
  let eventReg = guestEventRegistration;
  /**
   * When we add a guest to the regCart we just give them a lastName. So when we set the current guest before
   * we open the guest details modal if they are missing a firstName set the instance last name to empty so it
   * doesn't populate with GUEST XX in the lastName field.
   */
  if (!personalInformation.firstName) {
    eventReg = setIn(guestEventRegistration, ['attendee', 'personalInformation', 'lastName'], '');
  }
  if (!guestEventRegistration.registrationTypeId) {
    eventReg = setIn(eventReg, ['registrationTypeId'], defaultRegistrationTypeId);
  }
  return {
    type: SET_CURRENT_GUEST_EVENT_REGISTRATION,
    payload: {
      currentGuestEventRegistration: eventReg || {}
    }
  };
}

/**
 * Calls the registration api to update the number of guests or guest info / answers.
 * Also, it handles any capacity related issues that may happen due to this update.
 * @param {RegCart} regCartWithGuestUpdates reg cart object with guests
 */
export function updateGuestsInRegCart(regCartWithGuestUpdates, shouldAddTemporaryGuestInfo = false) {
  return async (dispatch, getState) => {
    let response;
    LOG.debug('updateGuestsInRegCart', regCartWithGuestUpdates);
    const { accessToken, regCartStatus: { lastSavedRegCart, registrationIntent },
      clients: { regCartClient } } = getState();
    if (registrationIntent === SAVING_REGISTRATION) {
      return;
    }
    dispatch({ type: UPDATE_REG_CART_PENDING });
    const currentEventRegId = getEventRegistrationId(getState());
    const lastSavedAttendee = getIn(lastSavedRegCart, ['eventRegistrations', currentEventRegId, 'attendee']);
    let regCartToPost = setIn(regCartWithGuestUpdates, ['eventRegistrations', currentEventRegId, 'attendee'],
      lastSavedAttendee);
    // Remove admin infomation from regcart with updates
    regCartToPost = setAdminRegOnProductionSelection(lastSavedRegCart, regCartToPost);
    try {
      dispatch({
        type: UPDATE_REG_CART_PENDING
      });
      response = await regCartClient.updateRegCart(accessToken, regCartToPost);
      LOG.debug('updated event registrations for guests', regCartToPost);
      const lastGuestAdded = getGuestsOfRegistrant(regCartToPost, currentEventRegId).pop();
      // Add back the primary's info while saving to state
      const savedRegCart = response.regCart;
      const notSavedAttendee = getIn(regCartWithGuestUpdates, ['eventRegistrations', currentEventRegId, 'attendee']);
      let regCartWithUnsavedChanges = setIn(savedRegCart, ['eventRegistrations', currentEventRegId, 'attendee'],
        notSavedAttendee);
      // Put back unsaved Admin information
      regCartWithUnsavedChanges = setAdminRegOnProductionSelection(regCartWithGuestUpdates, regCartWithUnsavedChanges);
      dispatch({
        type: UPDATE_REG_CART_SUCCESS,
        payload: {
          regCart: regCartWithUnsavedChanges,
          savedRegCart,
          validationMessages: response.validationMessages
        }
      });
      if (shouldAddTemporaryGuestInfo) {
        await dispatch(setCurrentGuestEventRegistration(lastGuestAdded));
      }
      await Promise.all([dispatch(loadAvailableCapacityCounts()), dispatch(calculatePricing())]);
      if (lastGuestAdded) {
        // if current register still has a guest, check whether new guests added.
        Object.values(getGuestsOfRegistrant(regCartToPost, currentEventRegId)).forEach((eventReg) => {
          if (!Object.keys(lastSavedRegCart.eventRegistrations)
            .find(regCartEventRegistrationId => regCartEventRegistrationId === eventReg.eventRegistrationId)) {
            dispatch(populateVisibleProducts(eventReg.eventRegistrationId));
          }
        });
      }
      LOG.debug('update reg cart with guests success');
    } catch (error) {
      LOG.error('update reg cart with guests failed', error);
      dispatch(hideLoadingOnError());
      dispatch({ type: UPDATE_REG_CART_FAILURE, payload: { error } });
      throw error;
    }
    return response;
  };
}

export const clearTemporaryGuestInformation = () => {
  return (dispatch) => {
    dispatch({ type: CLEAR_CURRENT_GUEST_EVENT_REGISTRATION });
  };
};

export function updateGuestDetails(openGuestDetailsDialog) {
  return async (dispatch, getState) => {
    const { registrationForm: { regCart, currentGuestEventRegistration = undefined } }
      = getState();
    const currentGuestEventRegistrationId = currentGuestEventRegistration
      && currentGuestEventRegistration.eventRegistrationId;

    if (!(currentGuestEventRegistration || currentGuestEventRegistrationId)) {
      LOG.debug('Temporary guest info missing in state');
      return;
    }
    if (!regCart) {
      LOG.debug('RegCart missing in state');
      return;
    }
    const prevRegistrationTypeId = getIn(regCart, ['eventRegistrations', currentGuestEventRegistrationId,
      'registrationTypeId']);
    // if guest widget on same page as admission item set the admission item to default on save
    let guestEventRegistration = currentGuestEventRegistration;
    const hasNoAdmissionItem = size(getAdmissionItems(regCart, currentGuestEventRegistrationId)) === 0;
    // if guest can choose their own items then auto-assign them the default for their regType
    const isGuestProductSelectionEnabled = isGuestProductSelectionEnabledOnRegPath(getState());
    if (hasNoAdmissionItem && isGuestProductSelectionEnabled) {
      const defaultAdmissionItemId = getDefaultAdmissionItemIdSelectionForRegType(getState(),
        currentGuestEventRegistration.registrationTypeId);
      if (defaultAdmissionItemId) {
        guestEventRegistration = setIn(guestEventRegistration, ['productRegistrations'],
          push(guestEventRegistration.productRegistrations, {
            productId: defaultAdmissionItemId,
            productType: 'AdmissionItem',
            quantity: 1,
            requestedAction: 'REGISTER'
          }));
      }
    }
    const regCartWithUpdatedGuestDetails = setIn(regCart, ['eventRegistrations', currentGuestEventRegistrationId],
      guestEventRegistration);
    const currentRegistrationTypeId = getIn(guestEventRegistration, ['registrationTypeId']);
    try {
      await dispatch(updateGuestsInRegCart(regCartWithUpdatedGuestDetails, false));
      if (prevRegistrationTypeId !== currentRegistrationTypeId) {
        await dispatch(populateVisibleProducts(currentGuestEventRegistrationId));
      }
      await dispatch(clearTemporaryGuestInformation());
    } catch (error) {
      if (getUpdateErrors.isKnownError(error)) {
        return await dispatch(openKnownErrorDialog(
          findKnownErrorResourceKey(error.responseBody.validationMessages),
          null,
          openGuestDetailsDialog
        ));
      }
    }
  };
}
